import type {
    IConfigLog,
    IConfigLogDestination_ValidValuesOnly,
    IConfigLogLevel,
} from '../interface/config';
import { IConfigLogDestination } from '../interface/config';
import type { ILog } from '../interface/log';
import type { ILogger } from './log.if';
import { LOG_LEVEL } from './log.if';
const instantiatorConsole = () => import('./log.console');
const instantiatorLocalStorage = () => import('./log.ls');
const instantiatorSentry = () => import('./log.sentry');

/* IConfigLogLevel & LOG_LEVEL have same enum values, just cast */
const logLevelToConfigLogLevel = (level: LOG_LEVEL): IConfigLogLevel => level as number;
const configLogLevelsToLogLevels = (level: IConfigLogLevel[]): LOG_LEVEL[] => level as number[];

function _isLogLevelEnabled(config: IConfigLog | IConfigLogLevel[], logLevel: LOG_LEVEL): boolean {
    if (Array.isArray(config)) {
        const configArr = config;
        return configArr.includes(logLevelToConfigLogLevel(logLevel));
    } else {
        const configObj = config;
        return configObj.loggingLevelsEnabled.includes(logLevelToConfigLogLevel(logLevel));
    }
}

const LOGGER_RESOLVERS: Record<
    IConfigLogDestination_ValidValuesOnly,
    (config: IConfigLog) => Promise<ILogger | undefined>
> = {
    [IConfigLogDestination.Console]: async (config: IConfigLog) => {
        const logLevels = configLogLevelsToLogLevels(config.loggingLevelsEnabled);
        const localConfig = config.outputConfiguration[IConfigLogDestination.Console] ?? {};
        const isEnabled = config.outputDestinations.includes(IConfigLogDestination.Console);
        return isEnabled
            ? (await instantiatorConsole()).default(logLevels, localConfig)
            : undefined;
    },
    [IConfigLogDestination.LocalStorage]: async (config: IConfigLog) => {
        const logLevels = configLogLevelsToLogLevels(config.loggingLevelsEnabled);
        const localConfig = config.outputConfiguration[IConfigLogDestination.LocalStorage] ?? {};
        const isEnabled = config.outputDestinations.includes(IConfigLogDestination.LocalStorage);
        return isEnabled
            ? (await instantiatorLocalStorage()).default(logLevels, localConfig)
            : undefined;
    },
    [IConfigLogDestination.Remote_Sentry]: async (config: IConfigLog) => {
        const logLevels = configLogLevelsToLogLevels(config.loggingLevelsEnabled);
        const localConfig = config.outputConfiguration[IConfigLogDestination.Remote_Sentry] ?? {};
        const isEnabled = config.outputDestinations.includes(IConfigLogDestination.Remote_Sentry);
        return isEnabled ? (await instantiatorSentry()).default(logLevels, localConfig) : undefined;
    },
};

function logger(config: IConfigLog): ILog {
    let loggers: ILogger[] = [];
    const middlewares: ((message: string) => string)[] = [];

    /* Asynchronously resolve/build these loggers. Dynamically fetch logger only when needed */
    Promise.all(Object.values(LOGGER_RESOLVERS).map(fn => fn(config)))
        .then(newLoggers => {
            loggers = newLoggers.filter(x => x !== undefined) as ILogger[];
        })
        .catch((error: Error) => {
            /* eslint-disable-next-line no-console */
            console.error(`Failed to instantiate loggers: ${error.message}`);
        });

    const logAll = (logLevel: LOG_LEVEL, message: string) => {
        const logMessage =
            middlewares.length > 0
                ? middlewares.reduce(
                      (currentMessage, middleware) => middleware(currentMessage),
                      message,
                  )
                : message;

        if (logMessage.length <= 0) {
            /* Nothing to do */
            return;
        }

        for (const loggerEntry of loggers) {
            loggerEntry.log(logLevel, logMessage);
        }
    };

    const assertAll = (condition: boolean | number | string, message: string) => {
        for (const loggerEntry of loggers) {
            loggerEntry.assert(condition, message);
        }
    };

    const milestone = (msg: string) => {
        logAll(LOG_LEVEL.MILESTONE, msg);
    };
    const verbose = (msg: string) => {
        logAll(LOG_LEVEL.VERBOSE, msg);
    };
    const trace = (msg: string) => {
        logAll(LOG_LEVEL.TRACE, msg);
    };
    const info = (msg: string) => {
        logAll(LOG_LEVEL.INFO, msg);
    };
    const warn = (msg: string) => {
        logAll(LOG_LEVEL.WARN, msg);
    };
    const warning = (msg: string) => {
        logAll(LOG_LEVEL.WARN, msg);
    };
    const error = (msg: string) => {
        logAll(LOG_LEVEL.ERROR, msg);
    };
    const fatal = (msg: string) => {
        logAll(LOG_LEVEL.FATAL, msg);
    };
    const assert = (condition: boolean | number | string, msg: string | undefined) => {
        assertAll(condition, msg ?? 'Assertion check failed');
    };
    const noop = (message: string) => {
        /* No-Op */
    };
    const noopAssert = (condition: boolean | number | string, message: string | undefined) => {
        /* No-Op */
    };

    const module: ILog = {
        milestone: _isLogLevelEnabled(config, LOG_LEVEL.MILESTONE) ? milestone : noop,
        verbose: _isLogLevelEnabled(config, LOG_LEVEL.VERBOSE) ? verbose : noop,
        trace: _isLogLevelEnabled(config, LOG_LEVEL.TRACE) ? trace : noop,
        info: _isLogLevelEnabled(config, LOG_LEVEL.INFO) ? info : noop,
        warn: _isLogLevelEnabled(config, LOG_LEVEL.WARN) ? warn : noop,
        warning: _isLogLevelEnabled(config, LOG_LEVEL.WARN) ? warning : noop,
        error: _isLogLevelEnabled(config, LOG_LEVEL.ERROR) ? error : noop,
        fatal: _isLogLevelEnabled(config, LOG_LEVEL.FATAL) ? fatal : noop,
        assert: _isLogLevelEnabled(config, LOG_LEVEL.ASSERT) ? assert : noopAssert,

        addMiddleware: middleware => middlewares.push(middleware),
        removeMiddleware: middleware => delete middlewares[middlewares.indexOf(middleware)],
    };

    return module;
}

export default logger;
